import SelectedCheckBadge from '@src/components/common/SelectedCheckBadge';
import { useState } from 'react';
import styled, { css } from 'styled-components';

import { useVideoCreatorStore } from '../../stores-v2';
import {
  CAPTION_PREVIEW_SCRIM,
  useCaptionPreviewBackground,
} from './captionPreviewStyles';
import PresetPreviewText from './PresetPreviewText';

import type { Preset as PresetType } from '../textProcessor/TextBrandingService';

export default function PresetPreview({
  preset: template,
  onSelectPreset,
  selected,
  onSettingsClick,
  selectionOnly = false,
  previewBackground,
  badge,
  'data-testid': dataTestId,
}: {
  preset: PresetType;
  onSelectPreset: (template: PresetType) => void;
  selected?: boolean;
  /**
   * PLAT-11877 — a word beside the title, in the title's own quiet type:
   * "Default" on the card a first "captions on" applies, so the answer is
   * on screen before the click.
   */
  badge?: string;
  onSettingsClick?: () => void;
  /**
   * Frame to preview the style over. Pass it when the host already holds the
   * store the frame comes from — a modal reads `useVideoCreatorStore()` through
   * a portal and can resolve a different (empty) one, which silently falls the
   * card back to the packaged backdrop while the panel beside it shows the real
   * clip. Omitted, the card resolves the frame itself.
   */
  previewBackground?: string;
  /**
   * The click only *stages* a selection that something else commits (a modal's
   * Replace button), rather than applying the template immediately.
   *
   * Selection is then shown the way the design system's Selectable Items
   * standard prescribes — a gold check badge — instead of the hover "Apply"
   * pill, which promises the template is being applied on the spot.
   */
  selectionOnly?: boolean;
  'data-testid'?: string;
}) {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const videoCreator = useVideoCreatorStore();
  // The hook resolves '' when no frame is available, so `||` (not `??`) has to
  // decide here — an empty host value still means "fall back".
  const ownBackground = useCaptionPreviewBackground(videoCreator);
  const presetBackground = previewBackground || ownBackground;

  if (!template) {
    return null;
  }

  const handleClick = () => {
    if (selected && onSettingsClick) {
      onSettingsClick();
    } else {
      onSelectPreset(template);
    }
  };

  return (
    <PresetWrapper data-testid={dataTestId}>
      <Preset
        presetBackground={presetBackground}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        aria-label={`Select ${template.title} template`}
        aria-pressed={selectionOnly ? !!selected : undefined}
        selected={selected}
        $selectionOnly={selectionOnly}
      >
        {selectionOnly && selected && <SelectedCheckBadge />}
        {isHovered && !selectionOnly ? (
          <>
            <PresetDimOverlay />
            <GlassButtonWrapper>
              <GlassButton>
                {selected && onSettingsClick ? 'Settings' : 'Apply'}
              </GlassButton>
            </GlassButtonWrapper>
          </>
        ) : selectionOnly ? (
          // Same scrim as the Captions panel thumbnail, so a style previews
          // identically in the panel and in the Replace Captions modal.
          <PresetScrim />
        ) : (
          <PresetDimOverlayLight />
        )}
        <VerticalAlignWrapper>
          <PresetPreviewText template={template} />
        </VerticalAlignWrapper>
      </Preset>
      <PresetText>
        {template.title}
        {badge && <PresetBadge data-testid="preset-badge">{badge}</PresetBadge>}
      </PresetText>
    </PresetWrapper>
  );
}

const PresetWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const PresetText = styled.div`
  color: var(--text-secondary);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-style: normal;
  font-weight: var(--font-weight-normal);
  line-height: 159%; /* 15.9px */
`;

// Not a bordered pill (design system: badges are not bordered) — a second,
// quieter word after the name, separated by a middle dot.
const PresetBadge = styled.span`
  color: var(--text-muted);
  &::before {
    content: ' · ';
  }
`;

const Preset = styled.div<{
  presetBackground?: string;
  selected?: boolean;
  $selectionOnly?: boolean;
}>`
  width: 100%;
  /* 16:9 — the frame captions are rendered into. The 145px cap stays for the
     rail's narrow columns, but a modal grid sizes its own cells. */
  ${props => !props.$selectionOnly && 'max-width: 145px;'}
  min-width: 0;
  aspect-ratio: 16 / 9;
  flex-shrink: 0;
  position: relative;
  box-sizing: border-box;
  cursor: pointer;
  z-index: 0;
  ${props =>
    props.presetBackground
      ? css`
          background-image: url(${props.presetBackground});
        `
      : css`
          background-image: url('/img/karaoke-preset-bg.png');
        `}
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #000;
  border-radius: var(--radius-input);
  border: 1px solid var(--border);
  ${props =>
    props.selected &&
    css`
      border-color: var(--primary);
      /* The doubled glow ring is dropped where a check badge already carries
         the selection — see the Selectable Items "no visual clutter" rule. */
      ${!props.$selectionOnly && 'box-shadow: 0 0 0 1px var(--primary);'}
    `}
  &:hover {
    border-color: var(--primary);
    ${props => !props.$selectionOnly && 'box-shadow: 0 0 0 1px var(--primary);'}
  }
`;

const GlassButtonWrapper = styled.div`
  position: absolute;
  display: flex;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;
`;

const GlassButton = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all var(--transition-normal) ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

const PresetDimOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.56);
  border-radius: var(--radius-input);
  z-index: 2;
  pointer-events: none;
`;

const PresetDimOverlayLight = styled(PresetDimOverlay)`
  background: rgba(0, 0, 0, 0.2);
  z-index: 0;
`;

const PresetScrim = styled(PresetDimOverlay)`
  background: ${CAPTION_PREVIEW_SCRIM};
  z-index: 0;
`;

const VerticalAlignWrapper = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  z-index: 1;
`;
